/**
 * Theme definitions for Storyteller.
 *
 * Each theme owns the same semantic token contract and only sets source
 * CSS variables. `main.css` derives the legacy `--theme-*` color values from
 * these source tokens so runtime theme switching only needs to update :root.
 */

const REQUIRED_THEME_TOKENS = [
  'surface',
  'surface-solid',
  'surface-variant',
  'on-surface',
  'on-surface-variant',
  'primary',
  'primary-hover',
  'ambient-secondary',
  'on-primary',
  'secondary',
  'secondary-hover',
  'on-secondary',
  'placeholder',
  'success',
  'on-success',
  'success-hover',
  'warning',
  'on-warning',
  'warning-hover',
  'danger',
  'on-danger',
  'danger-hover',
  'favorite',
  'border-color',
  'border-light',
]

interface TokenValue {
  rgb: string
  alpha?: number
}

interface ThemeTokens {
  [key: string]: TokenValue
}

interface ThemePreview {
  bg: string
  accent: string
  text: string
}

interface ThemeDefinition {
  id: string
  label: string
  preview: ThemePreview
  vars: Record<string, string>
}

const SHARED_TOKENS: ThemeTokens = {
  'on-primary': { rgb: '255 255 255' },
  secondary: { rgb: '106 117 131' },
  'secondary-hover': { rgb: '76 83 94' },
  'on-secondary': { rgb: '255 255 255' },
  placeholder: { rgb: '255 0 234' },
  success: { rgb: '94 252 141' },
  'on-success': { rgb: '0 11 31' },
  'success-hover': { rgb: '71 194 108' },
  warning: { rgb: '255 172 63' },
  'on-warning': { rgb: '0 11 31' },
  'warning-hover': { rgb: '196 128 40' },
  danger: { rgb: '255 85 82' },
  'on-danger': { rgb: '255 255 255' },
  'danger-hover': { rgb: '185 68 66' },
  favorite: { rgb: '255 58 100' },
  'border-color': { rgb: '221 221 221' },
  'border-light': { rgb: '221 221 221' },
}

function createTheme({ id, label, preview, tokens }: {
  id: string
  label: string
  preview: ThemePreview
  tokens: ThemeTokens
}): ThemeDefinition {
  const resolvedTokens = { ...SHARED_TOKENS, ...tokens }

  for (const token of REQUIRED_THEME_TOKENS) {
    if (!resolvedTokens[token]?.rgb) {
      throw new Error(`Theme "${id}" is missing required token "${token}"`)
    }
  }

  const vars: Record<string, string> = {}
  for (const [token, value] of Object.entries(resolvedTokens)) {
    vars[`--theme-${token}-rgb`] = value.rgb
    vars[`--theme-${token}-alpha`] = String(value.alpha ?? 1)
  }

  return { id, label, preview, vars }
}

export const themes: Record<string, ThemeDefinition> = {
  dark: createTheme({
    id: 'dark',
    label: 'Dark',
    preview: { bg: '#090c10', accent: '#1d8aff', text: '#f3f0ff' },
    tokens: {
      surface: { rgb: '9 12 16', alpha: 0.663 },
      'surface-solid': { rgb: '9 12 16' },
      'surface-variant': { rgb: '59 66 75', alpha: 0.8 },
      'on-surface': { rgb: '243 240 255' },
      'on-surface-variant': { rgb: '165 164 172' },
      primary: { rgb: '29 138 255' },
      'primary-hover': { rgb: '5 59 116' },
      'ambient-secondary': { rgb: '139 92 246' },
    },
  }),
  midnight: createTheme({
    id: 'midnight',
    label: 'Midnight',
    preview: { bg: '#0a0e1a', accent: '#7c8cff', text: '#e0e4f0' },
    tokens: {
      surface: { rgb: '10 14 26', alpha: 0.7 },
      'surface-solid': { rgb: '10 14 26' },
      'surface-variant': { rgb: '40 48 80', alpha: 0.8 },
      'on-surface': { rgb: '224 228 240' },
      'on-surface-variant': { rgb: '138 144 176' },
      primary: { rgb: '124 140 255' },
      'primary-hover': { rgb: '58 66 128' },
      'ambient-secondary': { rgb: '110 231 249' },
    },
  }),
  parchment: createTheme({
    id: 'parchment',
    label: 'Parchment',
    preview: { bg: '#1a1610', accent: '#c49a5c', text: '#e8dcc8' },
    tokens: {
      surface: { rgb: '26 22 16', alpha: 0.7 },
      'surface-solid': { rgb: '26 22 16' },
      'surface-variant': { rgb: '70 58 40', alpha: 0.75 },
      'on-surface': { rgb: '232 220 200' },
      'on-surface-variant': { rgb: '160 144 120' },
      primary: { rgb: '196 154 92' },
      'primary-hover': { rgb: '122 94 52' },
      'ambient-secondary': { rgb: '143 191 122' },
    },
  }),
  forest: createTheme({
    id: 'forest',
    label: 'Forest',
    preview: { bg: '#0c1410', accent: '#4ec98b', text: '#d8efe0' },
    tokens: {
      surface: { rgb: '12 20 16', alpha: 0.7 },
      'surface-solid': { rgb: '12 20 16' },
      'surface-variant': { rgb: '32 60 44', alpha: 0.75 },
      'on-surface': { rgb: '216 239 224' },
      'on-surface-variant': { rgb: '122 172 144' },
      primary: { rgb: '78 201 139' },
      'primary-hover': { rgb: '40 102 72' },
      'ambient-secondary': { rgb: '198 161 91' },
    },
  }),
}

export const themeIds = Object.keys(themes)
export const defaultThemeId = 'dark'
